import axios from 'axios';

const apiClient = axios.create({
    baseURL: process.env.REACT_APP_BASE_API_URL
});

export const apiClientEosNative = axios.create({
    baseURL: process.env.REACT_APP_NATIVE_BACKEND_API_URL
});

// Updated to use a function that gets token dynamically
export const addAccessTokenInterceptor = (
    getAccessToken: () => Promise<string>, // Function instead of static token
    user: string,
    onAuthError?: () => void // Optional callback for auth failures
) => {
    // Remove any existing interceptors to avoid duplicates
    apiClient.interceptors.request.clear();
    apiClientEosNative.interceptors.request.clear();

    // Request interceptor for main API
    apiClient.interceptors.request.use(
        async (config) => {
            try {
                const jwtToken = await getAccessToken();
                if (config && config.headers) {
                    config.headers.Authorization = `Bearer ${jwtToken}`;
                    config.headers.user = user;
                }
            } catch (error) {
                console.error('Failed to get access token:', error);
                onAuthError?.();
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    // Request interceptor for native API
    apiClientEosNative.interceptors.request.use(
        async (config) => {
            try {
                const jwtToken = await getAccessToken();
                if (config && config.headers) {
                    config.headers.Authorization = `Bearer ${jwtToken}`;
                    config.headers.user = user;
                }
            } catch (error) {
                console.error('Failed to get access token:', error);
                onAuthError?.();
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    // Optional: Add response interceptors to handle 401s
    const handleAuthError = async (error: any) => {
        if (error.response?.status === 401) {
            console.error('Authentication failed - token may be expired');
            onAuthError?.();
        }
        return Promise.reject(error);
    };

    apiClient.interceptors.response.use(
        (response) => response,
        handleAuthError
    );

    apiClientEosNative.interceptors.response.use(
        (response) => response,
        handleAuthError
    );
};

export const addAccessTokenInterceptorWithRetry = (
    getAccessToken: (options?: { cacheMode?: 'on' | 'off' | 'cache-only' }) => Promise<string>,
    user: string,
    onAuthError?: () => void
) => {
    apiClient.interceptors.request.clear();
    apiClientEosNative.interceptors.request.clear();

    // Track if we're currently refreshing to prevent multiple simultaneous refreshes
    let isRefreshing = false;
    let failedQueue: Array<{
        resolve: (token: string) => void;
        reject: (error: any) => void;
    }> = [];

    const processQueue = (error: any, token: string | null = null) => {
        failedQueue.forEach(promise => {
            if (error) {
                promise.reject(error);
            } else {
                promise.resolve(token!);
            }
        });
        failedQueue = [];
    };

    const createRequestInterceptor = (client: typeof apiClient) => {
        return client.interceptors.request.use(
            async (config) => {
                try {
                    const jwtToken = await getAccessToken();
                    
                    if (!jwtToken || jwtToken.trim() === '') {
                        console.error('Empty token received');
                        return Promise.reject(new Error('No valid token available'));
                    }
                    
                    if (config && config.headers) {
                        config.headers.Authorization = `Bearer ${jwtToken}`;
                        config.headers.user = user;
                    }
                } catch (error) {
                    console.error('Failed to get access token:', error);
                    return Promise.reject(error);
                }
                return config;
            },
            (error) => Promise.reject(error)
        );
    };

    const createResponseInterceptor = (client: typeof apiClient) => {
        return client.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config as any;

                // Only retry on 401 and if not already retried
                if (error.response?.status !== 401 || originalRequest._retry) {
                    return Promise.reject(error);
                }

                // If we're already refreshing, queue this request
                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    })
                        .then(token => {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                            return client.request(originalRequest);
                        })
                        .catch(err => {
                            return Promise.reject(err);
                        });
                }

                // Mark this request as being retried
                originalRequest._retry = true;
                isRefreshing = true;

                try {
                    // CRITICAL: Force cache bypass to get a fresh token
                    const freshToken = await getAccessToken({ cacheMode: 'off' });
                    
                    if (!freshToken || freshToken.trim() === '') {
                        throw new Error('Failed to get fresh token');
                    }

                    // Update the failed request with new token
                    originalRequest.headers.Authorization = `Bearer ${freshToken}`;
                    
                    // Process any queued requests with the new token
                    processQueue(null, freshToken);
                    
                    // Retry the original request
                    return client.request(originalRequest);
                    
                } catch (refreshError) {
                    console.error('Token refresh failed:', refreshError);
                    processQueue(refreshError, null);
                    
                    // Only call onAuthError if refresh truly failed
                    onAuthError?.();
                    
                    return Promise.reject(error);
                } finally {
                    isRefreshing = false;
                }
            }
        );
    };

    // Apply interceptors to both clients
    createRequestInterceptor(apiClient);
    createRequestInterceptor(apiClientEosNative);
    createResponseInterceptor(apiClient);
    createResponseInterceptor(apiClientEosNative);
};

export default apiClient;